import { UnitType } from "generated-graphql/graphql";

import { FormValues } from "./reportingFacilityChemicalFormValues";

/** What a saved reporting-unit override needs: nothing more. */
export type ReportingUnitOverrideInput = {
  unit: UnitType;
  reason: string;
};

/**
 * Turns the dialog's form state into the input the `setReportingUnitOverride`
 * mutation needs, or `null` when there is nothing valid to save. The caller is
 * responsible for the two outcomes this maps to: a `null` result while no unit
 * is selected means "clear any existing override"; a `null` result while a
 * unit *is* selected means the reason was missing and the submit must not
 * proceed at all.
 *
 * react-hook-form keeps values for inputs that have been hidden (the unit and
 * reason stay populated after the whole panel is collapsed), so this — not the
 * caller — is the single place that discards them before they can be
 * persisted. See the isExempt/isExemptReason/isExemptNote handling in
 * ReportingFacilityChemicalDialog.tsx for the same guard applied elsewhere.
 */
export function buildOverrideFromForm(data: FormValues): ReportingUnitOverrideInput | null {
  // Collapsing the panel must not immediately touch the server (see
  // resolveOverrideWrite's "clear" outcome), but on save there is nothing to
  // persist — a hidden unit/reason must not survive to become the saved
  // override, even though react-hook-form still carries their last values.
  if (!data.reportingUnitOverrideEnabled) {
    return null;
  }

  const unit = data.reportingUnitOverride;
  if (!unit) {
    return null;
  }

  const reason = data.reportingUnitOverrideReason ?? "";
  if (!reason.trim()) {
    return null;
  }

  return {
    unit,
    // Stored trimmed: the reason is quoted verbatim in the Summary Card banner
    // and the flag tooltip, and stray whitespace shows up in both.
    reason: reason.trim(),
  };
}

/**
 * What a submit should do about the reporting-unit override.
 *
 * - `skip`: leave the override alone entirely — either the user may not edit it,
 *   or there is no override row to remove, so there is nothing to ask the server
 *   for.
 * - `clear`: no unit is selected and an override row exists, so it goes away.
 * - `set`: call `setReportingUnitOverride` with this unit and reason.
 * - `block`: a unit is selected with no reason; the submit must not proceed.
 */
export type OverrideWrite =
  | { kind: "skip" }
  | { kind: "clear" }
  | ({ kind: "set" } & ReportingUnitOverrideInput)
  | { kind: "block" };

export function resolveOverrideWrite(
  data: FormValues,
  meta: { canEdit: boolean; hasExistingOverride: boolean }
): OverrideWrite {
  // The override block only renders for staff on an editable report, so anyone
  // else's submit carries whatever the form was seeded with and has no business
  // touching the override. Writing anyway would restamp a customer's save over
  // a decision they never made, and clearing the override outright if their
  // form happened to carry no unit.
  if (!meta.canEdit) {
    return { kind: "skip" };
  }
  if (!data.reportingUnitOverrideEnabled || !data.reportingUnitOverride) {
    // No unit selected is the state essentially every chemical is in, so a
    // blanket "clear" here made every staff save of this dialog call
    // clearReportingUnitOverride — which awaits a full inline aggregation for
    // the year in view and queues five more jobs, all to delete a row that was
    // never there. Only ask for the delete when there is something to delete.
    //
    // Keyed on the override row's existence, deliberately NOT on
    // `preOverrideUnit`: a refused override has a row (staff asked for it) but
    // no `preOverrideUnit` (aggregation declined it), and that is exactly the
    // state a staff member most needs to be able to clear.
    if (!meta.hasExistingOverride) {
      return { kind: "skip" };
    }
    return { kind: "clear" };
  }
  const override = buildOverrideFromForm(data);
  if (!override) {
    return { kind: "block" };
  }
  return { kind: "set", ...override };
}
